import { apiEsign } from "@/api/esign.api.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EsignDocumentRow } from "@/pages/esign/widgets/EsignDocumentRow.tsx";
import { useEsignEnvelope } from "@/queries/use-esign.query.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiEsignDocumentEnvelope,
  EsignRequestStatus,
  EsignSignatureType,
} from "@/types/esign.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  ProgressBar,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface EsignRequestDetailProps {
  envelope: ApiEsignDocumentEnvelope;
  documentId: number;
  documentTitle: string;
  onShowCertificate: (envelopeId: string, requestPublicId: string) => void;
}

export function EsignRequestDetail({
  envelope: listEnvelope,
  documentId,
  documentTitle,
  onShowCertificate,
}: EsignRequestDetailProps) {
  const { t } = useTranslation();
  const { renderShortDate, renderEmployeeName } = useFormatter();
  const { downloadFile } = useDownloadHelper();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const { data: envelope, isLoading } = useEsignEnvelope(listEnvelope.public_id);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const canDownloadReport =
    isFeatureEnabled(ApiFeatureType.esign) && canIEdit(ApiPermission.esign_download_report);

  const handleDownloadReport = (requestPublicId: string) => {
    setDownloadingId(requestPublicId);
    downloadFile({
      apiCall: () => apiEsign.getEvidenceReportPdf(listEnvelope.public_id),
      onFinally: () => setDownloadingId(null),
    });
  };

  const isSignature = listEnvelope.signature_type === EsignSignatureType.signature;
  const signedOfKey = isSignature ? "core:esign_signed_of" : "core:esign_acknowledged_of";

  const signedCount =
    envelope?.requests.filter((request) => request.status === EsignRequestStatus.signed).length ??
    0;
  const totalCount = envelope?.requests.length ?? 0;
  const progressPercent = totalCount === 0 ? 0 : Math.round((signedCount / totalCount) * 100);

  const detailsConfig = [
    { label: t("common:created"), value: renderShortDate(listEnvelope.datetime_created_at) },
    {
      label: t("core:esign_request_created_by"),
      value: envelope ? renderEmployeeName(envelope.created_by) : undefined,
    },
    {
      label: t("common:due_date"),
      value: envelope?.date_due ? renderShortDate(envelope.date_due) : "—",
    },
  ];

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Stack gap="sm">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-2 w-full" />
        </Stack>
        <Stack gap="sm">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </Stack>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-24 w-full" />
      </Stack>
    );
  }

  if (!envelope) return null;

  return (
    <>
      <Stack gap="xs">
        <Title header={t("core:esign_personas")} size="xs" />
        <Stack gap="sm" className="mt-4">
          <Stack direction="horizontal" items="center" justify="between">
            <Text className="text-title">
              {t(signedOfKey, { signed: signedCount, total: totalCount })}
            </Text>
            <Text className="text-secondary">{progressPercent}%</Text>
          </Stack>
          <ProgressBar
            completed={signedCount}
            total={totalCount}
            variant="status"
            tooltip={t(signedOfKey, { signed: signedCount, total: totalCount })}
          />
        </Stack>
      </Stack>

      <Stack gap="xs">
        <Title header={t("core:esign_signers")} size="xs" />
        <Stack>
          {envelope.requests.map((request) => {
            const fullName = request.signer
              ? `${request.signer.first_name} ${request.signer.last_name}`
              : t("core:esign_signer");
            const isSigned = request.status === EsignRequestStatus.signed;
            const isCancelled = request.status === EsignRequestStatus.cancelled;
            const isDeclined = request.status === EsignRequestStatus.declined;
            const avatarUrl = request.signer
              ? getEmployeeAvatarUrl(request.signer, "md")
              : undefined;
            return (
              <ListItem
                key={request.public_id}
                avatarUrl={avatarUrl}
                forceShowAvatar
                title={fullName}
                captionSlot={
                  <Text
                    className={
                      isSigned
                        ? "text-positive"
                        : isDeclined
                          ? "text-alert"
                          : isCancelled
                            ? "text-secondary"
                            : "text-warning"
                    }
                  >
                    {isSigned
                      ? isSignature
                        ? t("core:esign_status_signed")
                        : t("core:esign_status_acknowledged")
                      : isDeclined
                        ? request.decline_reason
                          ? `${t("core:esign_status_declined")}: ${request.decline_reason}`
                          : t("core:esign_status_declined")
                        : isCancelled
                          ? t("core:esign_status_cancelled")
                          : t("core:esign_status_pending")}
                  </Text>
                }
                valueSlot={
                  isSigned && canDownloadReport ? (
                    <DropdownMenu
                      trigger={
                        <ButtonIcon
                          variant="secondary"
                          icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                        />
                      }
                    >
                      <DropdownMenuItem
                        onClick={() => onShowCertificate(listEnvelope.public_id, request.public_id)}
                        title={t(
                          isSignature
                            ? "core:esign_show_signature_certificate"
                            : "core:esign_show_acknowledgment_certificate"
                        )}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-file-certificate" />}
                      />
                      <DropdownMenuItem
                        onClick={() => handleDownloadReport(request.public_id)}
                        disabled={downloadingId === request.public_id}
                        title={t("core:esign_download_report")}
                        leftIcon={
                          <FontAwesomeIcon
                            icon={
                              downloadingId === request.public_id
                                ? "fa-light fa-spinner"
                                : "fa-light fa-cloud-arrow-down"
                            }
                            className={
                              downloadingId === request.public_id
                                ? "animate-[spin_7s_linear_infinite]"
                                : undefined
                            }
                          />
                        }
                      />
                    </DropdownMenu>
                  ) : undefined
                }
              />
            );
          })}
        </Stack>
      </Stack>

      <EsignDocumentRow
        documentId={documentId}
        documentTitle={documentTitle}
        showFields={isSignature}
      />

      <Stack>
        <Title header={t("common:details")} size="xs" />
        {detailsConfig.map((row) => (
          <ListItem key={row.label} title={row.label} caption={row.value ?? "-"} />
        ))}
      </Stack>
    </>
  );
}
